/**
 * Métriques de police : texte centré optiquement (retour de Luc du 04/10, étiquettes et listes « texte trop haut »).
 *
 * Constat (audit à l'encre, _tools/ux-v2/align-audit.mjs) : dans une boîte de ligne, le navigateur place la ligne de
 * base à `ascent` du haut de la zone de contenu, haute de `ascent + descent`. Le cœur du texte (bande capitales →
 * ligne de base) est donc décalé de (ascent − descent − capitales) / 2 par rapport au centre de sa boîte. Avec
 * Helvetica Neue LT (police du thème monday-2026) : ascent 0,71 em, descent 0,29 em, capitales 0,714 em → le cœur est
 * 0,147 em AU-DESSUS du centre (1,8 px à 12 px) ; tout texte centré par sa boîte (flex, grille, padding symétrique)
 * dans une pilule, un bouton, un champ, ou à côté d'une icône, paraît trop haut. `text-box: trim-both cap alphabetic`
 * ne corrige pas cette police : la hauteur de capitale qu'elle déclare vaut la moitié de son encre.
 *
 * Correctif à la racine : un alias de la police (mêmes fichiers) dont `ascent-override` / `descent-override` posent la
 * ligne de base pour que ascent − descent = hauteur de capitale MESURÉE (encre du « H »), ascent + descent restant à
 * ± 5 % de celui de la police (choisi pour limiter l'arrondi au pixel de Chromium). Le cœur du texte tombe alors au
 * centre de sa boîte : centrer la boîte centre le texte, partout, sans compensation propre à un composant.
 *
 * Fonctions pures (aucun effet de bord au chargement) ; la partie navigateur est dans theme/fontAlias.ts.
 */

/** Métriques d'une police, en em (fraction de la taille). */
export interface FontMeasure {
  ascent: number;
  descent: number;
  capHeight: number;
}

export interface MetricOverrides {
  /** Valeurs des descripteurs `ascent-override` / `descent-override` (pourcentages de la taille). */
  ascentOverride: string;
  descentOverride: string;
  /** Décalage corrigé, en em : cœur du texte − centre de la boîte AVANT correction (négatif = trop haut). */
  offset: number;
}

/** En deçà (en em), la police est jugée centrée : 0,01 em = 0,6 px à 62 px, le plus grand titre de la v2. */
export const CENTERED_TOLERANCE_EM = 0.01;

/** Décalage du cœur du texte par rapport au centre de sa boîte de ligne, en em (négatif = trop haut). */
export function capCenterOffset({ ascent, descent, capHeight }: FontMeasure): number {
  return (ascent - descent - capHeight) / 2;
}

const round4 = (n: number) => Math.round(n * 10000) / 10000;

/**
 * Repli sans ascent-override (Safari, theme/fontAlias.ts supportsMetricOverrides) : décalage vers le bas, en em, qui
 * ramène le cœur du texte au centre de sa boîte (Helvetica Neue LT : 0,147 em) ; null si la police est déjà centrée (à
 * CENTERED_TOLERANCE_EM près), si son texte est trop BAS (le repli ne fait que descendre), ou si les mesures sont
 * illisibles.
 */
export function textNudge(measure: FontMeasure): number | null {
  const { ascent, descent, capHeight } = measure;
  const valid = [ascent, descent, capHeight].every((n) => Number.isFinite(n) && n >= 0);
  if (!valid || capHeight <= 0 || ascent <= 0 || capHeight > ascent + descent) return null;
  const offset = capCenterOffset(measure);
  return offset <= -CENTERED_TOLERANCE_EM ? round4(-offset) : null;
}

/** Tailles de texte de la v2 (styles, 9 à 22 px) : celles dont l'arrondi des métriques compte. */
export const UI_FONT_SIZES = [9, 9.5, 10, 10.5, 11, 11.5, 12, 12.5, 13, 13.5, 14, 14.5, 15, 16, 17, 19, 22] as const;

/** Arrondi de Chromium (SkScalarRoundToScalar) : ascent et descent de la police sont posés au pixel entier. */
const roundPx = (n: number) => Math.floor(n + 0.5);

/**
 * Écart (px) du cœur du texte au centre de sa boîte à la taille `size`, une fois ascent et descent arrondis chacun au
 * pixel entier, comme le fait Chromium (WebKit garde les fractions : écart nul).
 */
export function roundingError(ascent: number, descent: number, size: number): number {
  return (roundPx(ascent * size) - ascent * size - (roundPx(descent * size) - descent * size)) / 2;
}

export interface OverrideOptions {
  tolerance?: number;
  /** Tailles sur lesquelles l'arrondi de Chromium est réduit au minimum. */
  sizes?: readonly number[];
  /** Variation admise de ascent + descent (hauteur de la boîte `normal`), en part : 0,05 = ± 5 %. */
  spread?: number;
}

/**
 * Descripteurs qui centrent le cœur du texte dans sa boîte : ascent' − descent' = capitales. La somme
 * ascent' + descent' (hauteur de la boîte `normal`) reste à ± 5 % de celle de la police, choisie pour que l'arrondi au
 * pixel de Chromium décale le moins possible le texte sur les tailles de la v2 (Helvetica Neue LT : 0,43 px au pire
 * avec la somme d'origine, 0,32 px ainsi). null si la police est déjà centrée (à la tolérance près) ou si les mesures
 * sont illisibles.
 */
export function centeredOverrides(measure: FontMeasure, options: OverrideOptions = {}): MetricOverrides | null {
  const { tolerance = CENTERED_TOLERANCE_EM, sizes = UI_FONT_SIZES, spread = 0.05 } = options;
  const { ascent, descent, capHeight } = measure;
  const valid = [ascent, descent, capHeight].every((n) => Number.isFinite(n) && n >= 0);
  if (!valid || capHeight <= 0 || ascent <= 0 || capHeight > ascent + descent) return null;
  const offset = capCenterOffset(measure);
  if (Math.abs(offset) < tolerance) return null;

  const sum = ascent + descent;
  let best = { sum, worst: Infinity, rms: Infinity };
  const steps = 400;
  for (let i = 0; i <= steps; i++) {
    // De la somme d'origine vers les bords : à égalité, la plus proche de l'origine l'emporte.
    const k = Math.ceil(i / 2) * (i % 2 === 0 ? 1 : -1);
    const candidate = sum * (1 + (spread * k) / (steps / 2));
    if (candidate <= capHeight) continue;
    const a = (candidate + capHeight) / 2;
    const d = (candidate - capHeight) / 2;
    const errors = sizes.map((size) => Math.abs(roundingError(a, d, size)));
    const worst = Math.max(0, ...errors);
    const rms = Math.sqrt(errors.reduce((acc, e) => acc + e * e, 0) / Math.max(1, errors.length));
    if (worst < best.worst - 1e-9 || (Math.abs(worst - best.worst) <= 1e-9 && rms < best.rms - 1e-9)) {
      best = { sum: candidate, worst, rms };
    }
  }
  const ascentOverride = (best.sum + capHeight) / 2;
  const descentOverride = (best.sum - capHeight) / 2;
  return {
    ascentOverride: `${round4(ascentOverride * 100)}%`,
    descentOverride: `${round4(descentOverride * 100)}%`,
    offset: round4(offset),
  };
}

/** Familles génériques CSS : jamais déclarées par @font-face, jamais aliasées. */
const GENERIC_FAMILIES = new Set([
  'serif',
  'sans-serif',
  'monospace',
  'cursive',
  'fantasy',
  'system-ui',
  'ui-serif',
  'ui-sans-serif',
  'ui-monospace',
  'ui-rounded',
  'math',
  'emoji',
  'fangsong',
  '-apple-system',
  'blinkmacsystemfont',
]);

export function isGenericFamily(family: string): boolean {
  return GENERIC_FAMILIES.has(family.trim().toLowerCase());
}

/** Pile CSS → noms de familles, sans guillemets (« 'Helvetica Neue LT', Arial, sans-serif » → 3 noms). */
export function parseFontStack(stack: string | null | undefined): string[] {
  if (typeof stack !== 'string') return [];
  const families: string[] = [];
  let current = '';
  let quote: string | null = null;
  const push = () => {
    const name = current.trim().replace(/^(['"])(.*)\1$/, '$2').trim();
    if (name) families.push(name);
    current = '';
  };
  for (const char of stack) {
    if (quote) {
      if (char === quote) quote = null;
      current += char;
    } else if (char === '"' || char === "'") {
      quote = char;
      current += char;
    } else if (char === ',') {
      push();
    } else {
      current += char;
    }
  }
  push();
  return families;
}

/** Nom de famille CSS entre guillemets simples. */
export function quoteFamily(family: string): string {
  return `'${family.replace(/\\/g, '\\\\').replace(/'/g, "\\'")}'`;
}

/** Nom de l'alias d'une famille : préfixe propre à la v2, jamais une famille du site. */
export function aliasFamilyName(family: string): string {
  return `mmv2 ${family}`;
}

/** Pile avec l'alias en tête ; la pile d'origine suit (repli tant que l'alias charge, ou s'il échoue). */
export function stackWithAlias(alias: string, stack: string, family?: string): string {
  // Juste devant la famille corrigée (pas en tête de pile) : une famille placée avant elle, absente aujourd'hui mais
  // servie plus tard, garde sa priorité.
  if (family) {
    const families = parseFontStack(stack);
    const at = families.findIndex((f) => f.toLowerCase() === family.toLowerCase());
    if (at > 0) {
      const parts = splitStack(stack);
      if (parts.length === families.length) {
        return [...parts.slice(0, at), quoteFamily(alias), ...parts.slice(at)].join(', ');
      }
    }
  }
  return `${quoteFamily(alias)}, ${stack}`;
}

/** Éléments bruts d'une pile (guillemets gardés), séparés hors guillemets. */
function splitStack(stack: string): string[] {
  const parts: string[] = [];
  let current = '';
  let quote: string | null = null;
  for (const char of stack) {
    if (quote) {
      if (char === quote) quote = null;
      current += char;
    } else if (char === '"' || char === "'") {
      quote = char;
      current += char;
    } else if (char === ',') {
      if (current.trim()) parts.push(current.trim());
      current = '';
    } else {
      current += char;
    }
  }
  if (current.trim()) parts.push(current.trim());
  return parts;
}

/**
 * Une règle @font-face couvre-t-elle le latin de base (« H », U+0048) ? Sans unicode-range : oui. Les règles
 * partielles (ex. <Fonts /> de la v1 : U+0100-024F seulement) ne peignent pas le texte courant : les aliaser mettait en
 * tête de pile une police qui ne couvre pas « H » (texte resté sans correction, revue du 04/10).
 */
export function coversBasicLatin(unicodeRange: string | null | undefined): boolean {
  const value = (unicodeRange ?? '').trim();
  if (!value) return true;
  const H = 0x48;
  return value.split(',').some((part) => {
    const token = part.trim().toUpperCase().replace(/^U\+/, '');
    if (!token) return false;
    if (token.includes('?')) {
      const lo = parseInt(token.replace(/\?/g, '0'), 16);
      const hi = parseInt(token.replace(/\?/g, 'F'), 16);
      return H >= lo && H <= hi;
    }
    const [a, b] = token.split('-');
    const lo = parseInt(a, 16);
    const hi = b ? parseInt(b, 16) : lo;
    return Number.isFinite(lo) && Number.isFinite(hi) && H >= lo && H <= hi;
  });
}

/** `src` d'un @font-face avec des url() absolues (le constructeur FontFace les résout contre la page, pas la feuille). */
export function absolutizeFontSrc(src: string, baseUrl: string): string {
  return src.replace(/url\(\s*(['"]?)([^'")]+)\1\s*\)/g, (whole, _quote: string, url: string) => {
    try {
      return `url("${new URL(url.trim(), baseUrl).href}")`;
    } catch {
      return whole;
    }
  });
}
